import { useTranscriptionsState } from '@modules/transcriptions/useTranscriptionsState';
import { useCallback, useEffect, useMemo } from 'react';
import { useLocation } from 'react-router';
import { AICallChannel } from 'src/@types/chat';
import { Iconify } from 'src/components/iconify';
import { useWorkspaceFeaturesSettings } from 'src/hooks/useWorkspace';
import { useLocales } from 'src/locales';
import { useChatContext } from 'src/provider/ChatContext';
import { useLivekitRTCState } from 'src/provider/VoiceChannel/livekit/useLivekitRTCState';
import { getSectionPath } from 'src/state';
import { useChatAIContextState } from '../ChatAI';
import { useGatheringChatState } from '../GatheringSessions/gatheringChatState';
import { ActionsDrawer } from './ActionsDrawer';
import { AddActionBar } from './Add/AddActionBar';
import { ChatUnread } from './Chat/ChatUnread';
import { DocsActionBar } from './Docs';
import { useDocsActionBarUIState } from './Docs/useDocsActionBarUIState';
import { GatheringChatButton } from './Gathering/GatheringChat';
import { OpenedGatheringChats } from './Gathering/GatheringChat/OpenedGatheringChats';
import { GatheringParticipantsButton } from './Gathering/GatheringParticipants/GatheringParticipantsButton';
import { GatheringTranscription } from './Gathering/GatheringTranscription';
import { Support } from './Support';
import { TranscriptionsActionBar } from './Transcriptions';
import { actionsComponent, drawerSize } from './shared/contants';
import {
  ActionsBarButtonStyled,
  ActionsContainer,
  ActionsSession,
  ContainerStyled,
  GatheringSession,
} from './shared/styles';
import { Action } from './shared/types';
import { useActionBarState } from './useActionBarState';

export const ActionsBar = () => {
  const { translate } = useLocales();
  const location = useLocation();

  const { isOpen, setIsOpen, setReopen, openAction, setOpenAction } =
    useActionBarState();

  const { isChatOpen, toggleChat } = useChatAIContextState((s) => ({
    isChatOpen: s.isChatOpen,
    toggleChat: s.toggleChat,
  }));

  const { setCurrentActiveChat } = useChatContext();

  const { activeVoiceChannel, connectionStatus, activeSessionId } =
    useLivekitRTCState();

  const { notesList } = useTranscriptionsState();
  const { sessionId, chatSessionsList } = useGatheringChatState();
  const { docsIDList } = useDocsActionBarUIState();
  const openedChatSessionList = useMemo(
    () => Object.keys(chatSessionsList),
    [chatSessionsList],
  );
  const openedNotesList = useMemo(() => Object.keys(notesList), [notesList]);

  const { smartTodos } = useWorkspaceFeaturesSettings();

  const toggleOpenAction = useCallback(
    (action: Action, fromClose = false) => {
      if (openAction === action) {
        setIsOpen(false);
        if (action === 'openai') {
          toggleChat(false);
        }
        if ((action === 'chat' || action === 'gatheringChat') && !fromClose) {
          setReopen(action);
        }
      } else {
        if (isOpen) {
          setReopen(action);
          setIsOpen(false);
          return;
        }
        setOpenAction(action);
      }
    },
    [openAction, isOpen, toggleChat, setOpenAction, setIsOpen, setReopen],
  );

  const currentPage = useMemo(
    () => getSectionPath(location.pathname),
    [location.pathname],
  );

  const Component = actionsComponent[openAction!];

  const onCloseAction = (action: Action) => {
    setReopen(null);
    toggleOpenAction(action, true);
  };

  // biome-ignore lint/correctness/useExhaustiveDependencies:
  useEffect(() => {
    if (currentPage === 'ai' && openAction === 'openai') {
      setIsOpen(false);
    }
    if (currentPage === 'drive' && openAction === 'cloud') {
      setIsOpen(false);
    }
    if (currentPage === 'todo' && openAction === 'todo') {
      setIsOpen(false);
    }
    if (openAction !== 'chat') {
      setCurrentActiveChat('');
    }
  }, [currentPage, openAction, setCurrentActiveChat]);

  useEffect(() => {
    if (isChatOpen) {
      setOpenAction('openai');
    }
  }, [isChatOpen, setOpenAction]);

  const voiceConnected =
    activeVoiceChannel?.channel?._id &&
    !(activeVoiceChannel.channel as AICallChannel).aiCall &&
    connectionStatus === 'connected';

  return (
    <>
      <ActionsDrawer drawerSize={drawerSize[openAction!]}>
        {openAction && Component && (
          <Component onClose={() => onCloseAction(openAction)} />
        )}
      </ActionsDrawer>

      <ContainerStyled>
        <ActionsContainer>
          <ActionsSession>
            {currentPage !== 'ai' && (
              <ActionsBarButtonStyled
                variant="outlined"
                color={openAction === 'openai' ? 'primary' : 'inherit'}
                onClick={() => toggleOpenAction('openai')}
                tooltip={translate('actions_bar.ai_chat')}
                placement="left"
              >
                <Iconify icon="copera:ai" />
              </ActionsBarButtonStyled>
            )}

            {currentPage !== 'drive' && (
              <ActionsBarButtonStyled
                variant="outlined"
                color={openAction === 'cloud' ? 'primary' : 'inherit'}
                onClick={() => toggleOpenAction('cloud')}
                tooltip={translate('shared.labels.files')}
                placement="left"
              >
                <Iconify icon="tabler:photo-video" />
              </ActionsBarButtonStyled>
            )}

            {currentPage !== 'todo' && smartTodos && (
              <ActionsBarButtonStyled
                variant="outlined"
                color={openAction === 'todo' ? 'primary' : 'inherit'}
                onClick={() => toggleOpenAction('todo')}
                tooltip={translate('header.general_search.tabs.todos')}
                placement="left"
              >
                <Iconify icon="tabler:circle-check" />
              </ActionsBarButtonStyled>
            )}

            <AddActionBar />
          </ActionsSession>

          {voiceConnected && (
            <GatheringSession>
              <GatheringParticipantsButton
                isOpened={openAction === 'gathering'}
                toggleOpenAction={toggleOpenAction}
              />

              <GatheringChatButton
                sessionId={activeSessionId!}
                isOpened={
                  openAction === 'gatheringChat' &&
                  activeSessionId === sessionId
                }
                toggleOpenAction={toggleOpenAction}
                disabled={!activeSessionId}
              />

              <GatheringTranscription
                onClick={() => toggleOpenAction('notes')}
              />
            </GatheringSession>
          )}

          <ActionsSession>
            <OpenedGatheringChats
              toggleOpenAction={toggleOpenAction}
              showBorder={connectionStatus !== 'connected'}
            />

            <TranscriptionsActionBar
              onClick={() => toggleOpenAction('notes')}
              showBorder={
                !!openedChatSessionList.length ||
                connectionStatus !== 'connected'
              }
            />

            <DocsActionBar
              onClick={() => toggleOpenAction('doc')}
              showBorder={
                !!openedNotesList.length ||
                !!openedChatSessionList.length ||
                connectionStatus !== 'connected'
              }
            />

            <ChatUnread
              toggleOpenAction={toggleOpenAction}
              showBorder={
                !!openedNotesList.length ||
                !!openedChatSessionList.length ||
                !!docsIDList.length ||
                connectionStatus !== 'connected'
              }
            />
          </ActionsSession>
        </ActionsContainer>

        <Support />
      </ContainerStyled>
    </>
  );
};
